import React from 'react';

/* ════════════════════════════════════════════════════════════════
   ToolPreview — CSS / SVG mini mockups shown inside each tool card
   in the Create hub. Each preview is intentionally small (~80px tall),
   purely visual, no animations heavy enough to drop fps. They give
   the user a glance of what the tool produces before they click in.
   ════════════════════════════════════════════════════════════════ */

const LIME = '#c8f060';
const TEXT = '#f5f0e8';
const BG = '#0a0a0a';
const FRAME = 'rgba(255,255,255,0.04)';

const wrap = (children, extra = {}) => (
  <div style={{
    position: 'relative', width: '100%', height: 84,
    borderRadius: 8, overflow: 'hidden',
    background: BG, border: '1px solid rgba(255,255,255,0.06)',
    ...extra,
  }}>{children}</div>
);

const PREVIEWS = {
  'auto-campaign': () => wrap(
    <div style={{ position: 'absolute', inset: 0, padding: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
      <p style={{ fontSize: 7, color: LIME, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700 }}>Week of Feb 12</p>
      {[
        { d: 'M', t: 'Reel · curiosity hook' },
        { d: 'T', t: 'Email · re-engage list' },
        { d: 'W', t: 'Carousel · before/after' },
      ].map((x, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 7.5, color: TEXT }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: 'rgba(200,240,96,0.12)', color: LIME, display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 7 }}>{x.d}</span>
          <span style={{ opacity: 0.85 }}>{x.t}</span>
        </div>
      ))}
    </div>
  ),
  'content-studio': () => wrap(
    <div style={{ position: 'absolute', inset: 0, padding: 10 }}>
      <p style={{ fontSize: 6.5, color: 'rgba(245,240,232,0.4)', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 4 }}>CAPTION</p>
      <p style={{ fontSize: 8.5, color: TEXT, lineHeight: 1.45, fontStyle: 'italic' }}>What if your competitors stopped showing up tomorrow? You&rsquo;d have one shot at the audience they&rsquo;re stealing. They won&rsquo;t. But you can stop letting them out-publish you.</p>
    </div>
  ),
  'email-campaigns': () => wrap(
    <div style={{ position: 'absolute', inset: 6, borderRadius: 5, background: '#fafaf7', padding: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 3, marginBottom: 5 }}>
        <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#ef4444' }} />
        <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#f59e0b' }} />
        <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#22c55e' }} />
      </div>
      <p style={{ fontSize: 6, color: '#9ca3af', letterSpacing: '0.04em' }}>SUBJECT</p>
      <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 10, color: '#111', letterSpacing: '-0.01em', marginTop: 1 }}>We saved your spot.</p>
      <div style={{ marginTop: 5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[1, 0.85, 0.55].map((w, i) => <div key={i} style={{ height: 2, borderRadius: 99, background: 'rgba(0,0,0,0.08)', width: `${w * 100}%` }} />)}
      </div>
    </div>
  ),
  'voiceover': () => wrap(
    <div style={{ position: 'absolute', inset: 0, padding: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
      {Array.from({ length: 28 }).map((_, i) => {
        const h = Math.abs(Math.sin(i * 0.7)) * 50 + 10;
        return <div key={i} style={{ width: 2.5, height: `${h}%`, borderRadius: 2, background: LIME, opacity: 0.4 + Math.abs(Math.sin(i * 0.5)) * 0.6 }} />;
      })}
      <span style={{ position: 'absolute', bottom: 6, right: 8, fontSize: 7, color: 'rgba(245,240,232,0.4)', letterSpacing: '0.08em' }}>00:18</span>
    </div>
  ),
  '3d-visual-ads': () => wrap(
    <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 30% 35%, #fbbf24, #d97706 60%, #7c2d12)' }}>
      <div style={{ position: 'absolute', top: 14, right: 18, width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'absolute', top: 44, right: 30, width: 14, height: 14, borderRadius: '50%', background: 'rgba(255,255,255,0.24)' }} />
      <div style={{ position: 'absolute', bottom: 10, left: 12 }}>
        <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 11, color: '#fff7ed', letterSpacing: '-0.02em', lineHeight: 1 }}>BOLD SALE</p>
        <p style={{ fontSize: 6.5, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>40% OFF · SHOP NOW</p>
      </div>
    </div>
  ),
  'carousels': () => wrap(
    <div style={{ position: 'absolute', inset: 0, display: 'flex', gap: 4, padding: 8, overflow: 'hidden' }}>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} style={{
          flex: '0 0 30%',
          height: '100%',
          borderRadius: 5,
          background: i === 0 ? 'linear-gradient(160deg,#1e293b,#0f172a)' : i === 1 ? '#1f2937' : i === 2 ? '#374151' : '#0f172a',
          transform: `translateX(${i * 2}px)`,
          padding: 6,
          opacity: 1 - i * 0.18,
        }}>
          <p style={{ fontSize: 5.5, color: LIME, letterSpacing: '0.14em', textTransform: 'uppercase' }}>0{i + 1}</p>
          <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 8, color: '#f8fafc', letterSpacing: '-0.01em', marginTop: 3, lineHeight: 1.05 }}>Why solo<br />founders<br />win</p>
        </div>
      ))}
    </div>
  ),
  'flyer-creator': () => wrap(
    <div style={{ position: 'absolute', inset: 0, background: '#04050a', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: 10, overflow: 'hidden' }}>
      <p style={{ fontSize: 6.5, letterSpacing: '0.2em', color: LIME, textTransform: 'uppercase', fontWeight: 700 }}>Pop-up</p>
      <div>
        <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 13, color: TEXT, letterSpacing: '-0.02em', lineHeight: 0.95 }}>Saturday<br />only.</p>
      </div>
      <div style={{ alignSelf: 'flex-start', padding: '2px 7px', borderRadius: 999, background: LIME, color: BG, fontSize: 6, fontWeight: 800, letterSpacing: '0.08em' }}>RSVP →</div>
    </div>
  ),
  'brand-pack': () => wrap(
    <div style={{ position: 'absolute', inset: 0, padding: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {['#c8f060', '#0a0a0a', '#22d3ee', '#a78bfa'].map((c) => (
          <div key={c} style={{ width: 16, height: 16, borderRadius: 4, background: c, boxShadow: '0 0 0 1px rgba(255,255,255,0.06)' }} />
        ))}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 14, color: TEXT, letterSpacing: '-0.02em', lineHeight: 1 }}>Glow.</p>
        <p style={{ fontSize: 7, color: 'rgba(245,240,232,0.5)', letterSpacing: '0.08em', marginTop: 2 }}>Syne · Inter · #c8f060</p>
        <div style={{ marginTop: 4, height: 1.5, borderRadius: 99, background: LIME, width: '60%' }} />
      </div>
    </div>
  ),
  'template-studio': () => wrap(
    <div style={{ position: 'absolute', inset: 0, padding: 6, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 4 }}>
      {[
        { bg: '#fafaf7', fg: '#111' },
        { bg: '#04050a', fg: TEXT },
        { bg: '#c8f060', fg: BG },
        { bg: '#1e293b', fg: '#f8fafc' },
        { bg: '#fef3c7', fg: '#92400e' },
        { bg: '#0f172a', fg: '#c8f060' },
      ].map((t, i) => (
        <div key={i} style={{ borderRadius: 4, background: t.bg, padding: 4, display: 'flex', alignItems: 'flex-end' }}>
          <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 7, color: t.fg, letterSpacing: '-0.02em', lineHeight: 0.95 }}>Aa</p>
        </div>
      ))}
    </div>
  ),
  'character-creator': () => wrap(
    <div style={{ position: 'absolute', inset: 0, padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
      {[
        { bg: '#fbbf24', f: '😊' },
        { bg: '#a78bfa', f: '👋' },
        { bg: '#22d3ee', f: '✦' },
        { bg: '#c8f060', f: '!' },
      ].map((c, i) => (
        <div key={i} style={{
          width: 30, height: 30, borderRadius: '50%',
          background: `radial-gradient(circle at 30% 30%, ${c.bg}, rgba(0,0,0,0.4))`,
          display: 'grid', placeItems: 'center',
          fontSize: 12,
          transform: `translateY(${(i % 2) * 4}px) scale(${1 - i * 0.06})`,
          fontFamily: "'Syne', sans-serif", fontWeight: 800,
          color: BG,
        }}>{c.f}</div>
      ))}
    </div>
  ),
  'video-ads': () => wrap(
    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 70% 40%, rgba(200,240,96,0.18), transparent 60%)' }} />
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 26, height: 26, borderRadius: '50%', background: LIME, display: 'grid', placeItems: 'center', boxShadow: '0 4px 16px rgba(200,240,96,0.4)' }}>
        <svg width="9" height="9" viewBox="0 0 9 9"><path d="M2 1l5 3.5L2 8V1z" fill={BG} /></svg>
      </div>
      <div style={{ position: 'absolute', bottom: 6, left: 8, right: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ flex: 1, height: 2, background: 'rgba(255,255,255,0.18)', borderRadius: 99 }}>
          <span style={{ display: 'block', width: '38%', height: '100%', background: LIME, borderRadius: 99 }} />
        </span>
        <span style={{ fontSize: 6.5, color: 'rgba(255,255,255,0.6)' }}>00:08</span>
      </div>
    </div>
  ),
  'animation-series': () => wrap(
    <div style={{ position: 'absolute', inset: 0, padding: 6, display: 'flex', gap: 4 }}>
      {[
        { bg: '#7c3aed' }, { bg: '#06b6d4' }, { bg: '#f59e0b' },
      ].map((s, i) => (
        <div key={i} style={{ flex: 1, borderRadius: 5, position: 'relative', background: `linear-gradient(135deg, ${s.bg}, rgba(0,0,0,0.4))`, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, padding: 5 }}>
            <p style={{ fontSize: 5, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.12em' }}>EP {i + 1}</p>
            <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 7, color: '#fff', letterSpacing: '-0.01em', marginTop: 12, lineHeight: 1 }}>Mini<br />series</p>
          </div>
          <span style={{ position: 'absolute', bottom: 4, right: 4, fontSize: 5.5, color: '#fff', opacity: 0.7 }}>0:12</span>
        </div>
      ))}
    </div>
  ),
  'product-shoot-studio': () => wrap(
    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #f5f0e8 0%, #e7e0d2 100%)', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '45%', background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.08))' }} />
      <div style={{ position: 'absolute', left: '50%', bottom: 12, transform: 'translateX(-50%)', width: 28, height: 38, borderRadius: '4px 4px 14px 14px', background: 'linear-gradient(180deg, #fef9c3 0%, #fde68a 50%, #f59e0b 100%)', boxShadow: '0 4px 12px rgba(146,64,14,0.25)' }}>
        <div style={{ position: 'absolute', top: -3, left: '50%', transform: 'translateX(-50%)', width: 2, height: 6, background: '#fbbf24' }} />
        <div style={{ position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)', width: 4, height: 5, borderRadius: '50%', background: '#fb923c', boxShadow: '0 0 8px #fb923c' }} />
      </div>
      <p style={{ position: 'absolute', top: 6, left: 8, fontSize: 6, color: 'rgba(0,0,0,0.4)', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700 }}>Glow Candle</p>
    </div>
  ),
  'landing-page': () => wrap(
    <div style={{ position: 'absolute', inset: 0, padding: 8, background: '#fafaf7' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <div style={{ width: 12, height: 4, borderRadius: 99, background: BG }} />
        <div style={{ width: 14, height: 4, borderRadius: 99, background: LIME }} />
      </div>
      <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 11, color: '#111', letterSpacing: '-0.02em', lineHeight: 0.95 }}>Built<br />for solo<br />founders.</p>
      <div style={{ position: 'absolute', bottom: 8, left: 8, padding: '3px 8px', borderRadius: 99, background: BG, color: TEXT, fontSize: 5.5, fontWeight: 700, letterSpacing: '0.08em' }}>GET STARTED</div>
    </div>
  ),
};

export default function ToolPreview({ id }) {
  const Comp = PREVIEWS[id];
  if (!Comp) {
    return wrap(
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'rgba(245,240,232,0.15)', fontFamily: "'Syne', sans-serif", fontSize: 11 }}>—</div>
    );
  }
  return Comp();
}
